// Copyright (c) 2017 Deere & Company

import './index.scss';
import React, {
    Component,
    PropTypes
} from 'react';
import ClaimSearchResult from '@deere/cps-it.warranty-model-factory/lib/claim-search-result';
import Criteria from '../state/criteria';
import Dealer from '@deere/cps-it.warranty-model-factory/lib/dealer';
import FavoriteIcon from '../../../shared/components/favorites/favorite-icon';
import {FormattedMessage} from 'react-intl';
import KeyValuePair from '@deere/cps-it.warranty-model-factory/lib/key-value-pair';
import ReportContainer from './report-container';
import ReportCriteria from './report-criteria';
import ReportLayout from '@deere/cps-it.warranty-model-factory/lib/report-layout';
import ShowNoResultsFound from '../../shared/components/show-no-results-found/show-no-results-found';
import User from '@deere/cps-it.warranty-model-factory/lib/user';
import WarrantyLoadingIndicator from '../../../shared/components/warranty-loading-indicator';
import {canRunReport} from '../report-services/services';
import {getAdvancedClaimSummaryColumnDefinitions} from '../report-services/column-definitions';
import {getDefaultReportLayout} from '../../shared/services/report-variant';
import {scrollToAndResizeReport} from '../../shared/services/scroll-window';

function handleAllClaimStatusesSelect() {

    this.actions.selectAllStatuses(this.user.settings.claims.status.allowedStatuses);

}

function handleClaimStatusSelect(status, isSelected) {

    this.actions.selectStatus(status, isSelected);

}

function handleCriteriaDateChange(dates) {

    this.actions.updateClaimCreatedDates(dates);

}

function handleCriteriaInputChange(property, event) {

    this.actions.updateCriterion(property, event.target.value);

}

function handleCriteriaReset() {

    this.actions.resetReportCriteria();
    this.actions.selectAllDealersOnLoad(this.user.settings.dealer.accounts.selectedAccounts);
    this.actions.selectStatusesOnLoad(this.user.settings.claims.status.allowedStatuses);

}

function handleDealerSelect(dealer, isSelected) {

    this.actions.selectDealer(dealer, isSelected);

}

function handleRunReport() {

    const params = {
        criteria: this.criteria,
        gridApi: this.gridApi,
        reportLength: this.report.length,
        selectedDealers: this.selectedDealers,
        selectedStatuses: this.selectedStatuses
    };

    this.actions.toggleReportIsScrollable();
    this.actions.createReport(params);

}

function handleSelectAllDealers() {

    this.actions.selectAllDealers(this.user.settings.dealer.accounts.selectedAccounts);

}

function handleGridApiDefinition(params) {

    let defaultColumnState,
        defaultSortModel;

    this.actions.createAgGridApi(params.api, params.columnApi);

    if (this.selectedLayout && this.selectedLayout.name && !this.isInitialLoad) {

        const defaultLayout = this.layouts.find(layout => !layout.name);

        defaultColumnState = defaultLayout.columnState;
        defaultSortModel = defaultLayout.sortModel;

    } else {

        defaultColumnState = params.columnApi.getColumnState();
        defaultSortModel = params.api.getSortModel();

        this.actions.updateReportProperty('isInitialLoad', false);

    }

    this.actions.updateDefaultLayout(defaultColumnState, defaultSortModel);

    if (this.selectedLayout && this.selectedLayout.name) {

        params.columnApi.setColumnState(this.selectedLayout.columnState);
        params.api.setSortModel(this.selectedLayout.sortModel);
        params.api.setFilterModel(this.selectedLayout.filterModel);

    } else {

        const defaultLayout = getDefaultReportLayout(defaultColumnState, defaultSortModel);

        this.actions.updateReportProperty('selectedLayout', defaultLayout);

    }

}

function handleHideReportColumn(columnEvent) {

    if (!columnEvent.visible) {

        this.actions.hideReportColumn(this.columnApi.getColumnState());

    }

}

function handlePrintClaims(claims) {

    this.actions.displayClaims(claims, this.user.getLocale());

}

function handleUpdateShowSelectAll(visibility) {

    this.actions.updateShowSelectAll(visibility);

}

function handleUpdateDisabled(isDisabled) {

    this.actions.updateDisabled(isDisabled);

}

class AdvancedClaimSummaryReport extends Component {

    async componentWillMount() {

        this.props.actions.selectAllDealersOnLoad(this.props.user.settings.dealer.accounts.selectedAccounts);
        await this.props.actions.selectStatusesOnLoad(this.props.user.settings.claims.status.allowedStatuses);
        await this.props.actions.getReportLayouts('advanced-claim-summary');
        this.props.actions.updateGridLayouts(this.props.layouts, getAdvancedClaimSummaryColumnDefinitions());

    }

    componentDidUpdate(prevProps) {

        if (this.props.reportIsScrollable && this.props.report.length) {

            this.props.actions.resetReportIsScrollable();
            scrollToAndResizeReport();

        }
        const isFirstRun = !prevProps.report.length && !prevProps.showNoResultsFound;
        const layoutWasChanged = this.props.selectedLayout !== prevProps.selectedLayout;
        const readyToApplyLayout = this.props.selectedLayout && Object.keys(this.props.columnApi).length && this.props.isDefaultColumnStateAvailable;

        if ((isFirstRun || layoutWasChanged) && readyToApplyLayout) {

            this.props.columnApi.setColumnState(this.props.selectedLayout.columnState);
            this.props.gridApi.setSortModel(this.props.selectedLayout.sortModel);
            this.props.gridApi.setFilterModel(this.props.selectedLayout.filterModel);

        }

    }

    componentWillUnmount() {

        this.props.actions.resetReport();
        this.props.actions.updateShowNoResultsFound(false);
        this.props.actions.resetReportVariant();

    }

    render() {

        const reportVariant = {
            isInitialLoad: this.props.isInitialLoad,
            isReportColumnsVisible: this.props.isReportColumnsVisible,
            layouts: this.props.layouts,
            selectedLayout: this.props.selectedLayout
        };

        return (
            <section className='advanced-claim-summary-report'>
                <WarrantyLoadingIndicator isLoading={this.props.busyCounter > 0}/>
                <h2 className='advanced-claim-summary-report-title'>
                    <FormattedMessage id='ADVANCED_CLAIM_SUMMARY'/>
                    <FavoriteIcon
                        id='ADVANCED_CLAIM_SUMMARY'
                        route={this.props.pathName}
                    />
                </h2>
                <ReportCriteria
                    allowedStatuses={this.props.user.settings.claims.status.allowedStatuses}
                    canRunReport={canRunReport(this.props.criteria, this.props.selectedDealers, this.props.selectedStatuses)}
                    criteria={this.props.criteria}
                    onAllClaimStatusesSelect={this.props::handleAllClaimStatusesSelect}
                    onClaimStatusSelect={this.props::handleClaimStatusSelect}
                    onCriteriaDateChange={this.props::handleCriteriaDateChange}
                    onCriteriaInputChange={this.props::handleCriteriaInputChange}
                    onCriteriaReset={this.props::handleCriteriaReset}
                    onDealerSelect={this.props::handleDealerSelect}
                    onRunReport={this.props::handleRunReport}
                    onSelectAllDealers={this.props::handleSelectAllDealers}
                    selectedAccounts={this.props.user.settings.dealer.accounts.selectedAccounts}
                    selectedDealers={this.props.selectedDealers}
                    selectedStatuses={this.props.selectedStatuses}
                />
                {this.props.report.length ?
                    <ReportContainer
                        columnApi={this.props.columnApi}
                        gridApi={this.props.gridApi}
                        hideReportColumn={this.props::handleHideReportColumn}
                        isPrintClaimDisabled={this.props.isPrintClaimDisabled}
                        onCreateAgGridApi={this.props::handleGridApiDefinition}
                        onPrintClaims={this.props::handlePrintClaims}
                        onUpdateShowSelectAll={this.props::handleUpdateShowSelectAll}
                        report={this.props.report}
                        reportVariant={reportVariant}
                        showSelectAll={this.props.showSelectAll}
                        updateDisabled={this.props::handleUpdateDisabled}
                    /> :
                        <ShowNoResultsFound showNoResultsFound={this.props.showNoResultsFound}/>
                }
            </section>
        );

    }

}

AdvancedClaimSummaryReport.displayName = 'AdvancedClaimSummaryReport';
AdvancedClaimSummaryReport.propTypes = {
    actions: PropTypes.object.isRequired,
    busyCounter: PropTypes.number.isRequired,
    columnApi: PropTypes.object.isRequired,
    criteria: PropTypes.instanceOf(Criteria).isRequired,
    gridApi: PropTypes.object.isRequired,
    isDefaultColumnStateAvailable: PropTypes.bool.isRequired,
    isInitialLoad: PropTypes.bool.isRequired,
    isPrintClaimDisabled: PropTypes.bool.isRequired,
    isReportColumnsVisible: PropTypes.bool.isRequired,
    layouts: PropTypes.arrayOf(PropTypes.instanceOf(ReportLayout)).isRequired,
    pathName: PropTypes.string.isRequired,
    report: PropTypes.arrayOf(PropTypes.instanceOf(ClaimSearchResult)).isRequired,
    reportIsScrollable: PropTypes.bool.isRequired,
    selectedDealers: PropTypes.arrayOf(PropTypes.instanceOf(Dealer)).isRequired,
    selectedLayout: PropTypes.instanceOf(ReportLayout),
    selectedStatuses: PropTypes.arrayOf(PropTypes.instanceOf(KeyValuePair)).isRequired,
    showNoResultsFound: PropTypes.bool.isRequired,
    showSelectAll: PropTypes.bool.isRequired,
    user: PropTypes.instanceOf(User).isRequired
};

export default AdvancedClaimSummaryReport;



// WEBPACK FOOTER //
// ./src/reports/advanced-claim-summary/components/index.js